import { type NewContactData } from '@aikoa/common'
import { type Language } from '@aikoa/i18n'
import { useTranslations } from '@aikoa/i18n/react'
import parsePhoneNumber from 'libphonenumber-js/mobile'
import { useCallback, useEffect, useState } from 'react'
import { View } from 'react-native'
import { ContactConsentConfirmation } from '~/components/contacts/ContactConsentConfirmation'
import { LanguageInput } from '~/components/LanguageInput'
import { Body, Label } from '~/components/typography/Content.tsx'
import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils.ts'

export type ContactFormData = {
  name?: string
  email?: string
  phone?: string
  consent_confirmed?: boolean
  language?: Language
}

interface ContactFormProps {
  formData: ContactFormData
  onFormChange: (data: ContactFormData) => void
  autoFocus?: boolean
  className?: string
  showConsent?: boolean
}

function getDisplayPhoneNumber(phoneNumber: string) {
  try {
    return parsePhoneNumber(phoneNumber)?.formatInternational() ?? ''
  } catch (error: unknown) {
    console.error(error)
  }

  return phoneNumber
}

export function ContactForm({
  formData,
  onFormChange,
  autoFocus,
  className,
  showConsent = true,
}: ContactFormProps) {
  const { t } = useTranslations('contacts')
  const handleChange = useCallback(
    (field: keyof NewContactData, value: string | boolean) => {
      const processedValue =
        (field === 'email' || field === 'phone') && typeof value === 'string' && value.trim() === ''
          ? undefined
          : value

      onFormChange({ ...formData, [field]: processedValue })
    },
    [formData, onFormChange]
  )

  const [editPhoneValue, setEditPhoneValue] = useState(getDisplayPhoneNumber(formData.phone ?? ''))

  useEffect(() => {
    if (formData.phone) {
      setEditPhoneValue(getDisplayPhoneNumber(formData.phone))
    }
  }, [formData.phone])

  return (
    <View className={cn('gap-8', className)}>
      <View>
        <Label>{t(($) => $.form.nameLabel)}</Label>
        <Input
          value={formData.name ?? ''}
          onChangeText={(text) => handleChange('name', text)}
          placeholder={t(($) => $.form.namePlaceholder)}
          autoFocus={autoFocus}
          className="placeholder:text-tertiary-foreground"
        />
      </View>

      <View>
        <Label>{t(($) => $.form.phoneLabel)}</Label>
        <Input
          type="phone"
          value={editPhoneValue || ''}
          onChangeText={(text, _, storedPhoneNumber) => {
            setEditPhoneValue(text)
            handleChange('phone', storedPhoneNumber)
          }}
          className="placeholder:text-tertiary-foreground"
          placeholder={t(($) => $.form.phonePlaceholder)}
        />
      </View>

      <View>
        <Label>{t(($) => $.form.emailLabel)}</Label>
        <Input
          value={formData.email || ''}
          onChangeText={(text) => handleChange('email', text)}
          placeholder={t(($) => $.form.emailPlaceholder)}
          keyboardType="email-address"
          autoCapitalize="none"
          className="placeholder:text-tertiary-foreground"
        />
      </View>

      <View className="gap-2">
        <Label>{t(($) => $.form.languageLabel)}</Label>
        <Body>{t(($) => $.form.languageDescription)}</Body>
        <LanguageInput
          value={formData.language || 'fi'}
          onChange={(locale) => handleChange('language', locale)}
          showLabel
          triggerClassName="mt-2 w-auto flex-initial self-start border border-secondary/50"
          iconClassName="text-secondary"
          align="start"
        />
      </View>

      {showConsent && (
        <ContactConsentConfirmation
          checked={formData.consent_confirmed ?? false}
          onCheckedChange={(checked) => handleChange('consent_confirmed', checked)}
          label={t(($) => $.form.consentLabel)}
        />
      )}
    </View>
  )
}
